import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllProductsByAdmin,
  productReviewApprovalAction,
} from "store/features/product/productActions";
import {
  selectProductsByAdmin,
} from "store/features/product/productSelectors";
import {
  FiEdit,
  FiTrash2,
  FiPlus,
  FiChevronDown,
  FiChevronUp,
  FiSearch,
  FiEye,
  FiPackage,
  FiTrendingUp,
  FiCalendar,
} from "react-icons/fi";
import { FaIndianRupeeSign } from "react-icons/fa6";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Link } from "react-router-dom";

const Product = () => {
  const dispatch = useDispatch();
  const products = useSelector(selectProductsByAdmin);
 

  useEffect(() => {
    dispatch(fetchAllProductsByAdmin());
  }, [dispatch]);


  const [expandedRows, setExpandedRows] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");


  const toggleRow = (productId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const handleDelete = (productId) => {
    confirmAlert({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this product?",
      buttons: [
        {
          label: "Yes",
          onClick: () => console.log("Delete product", productId),
        },
        {
          label: "No",
        },
      ],
    });
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStockColor = (stock) => {
    if (stock === 0) return "bg-red-100 text-red-800 border-red-200";
    if (stock < 10) return "bg-yellow-100 text-yellow-800 border-yellow-200";
    return "bg-green-100 text-green-800 border-green-200";
  };


  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || product.status === statusFilter;
    return matchesSearch && matchesStatus;
  });


  const stats = {
    totalProducts: products.length,
    approvedProducts: products.filter((p) => p.status === "approved").length,
    totalRevenue: products.reduce(
      (sum, p) => sum + (p.discountPrice || p.price) * p.salesCount,
      0
    ),
    outOfStock: products.filter((p) => p.stock === 0).length,
  };


  const handleStatusChange = (productId, currentStatus) => {
  const newStatus = currentStatus === "approved" ? "rejected" : "approved";
  dispatch(productReviewApprovalAction(productId, newStatus))
    .then(() => {
      // Refresh the products list after successful status change
      dispatch(fetchAllProductsByAdmin());
    });
};


  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="mb-2 text-3xl font-bold text-gray-900">
                Products
              </h1>
              <p className="text-gray-600">
                Manage your product inventory and listings
              </p>
            </div>
            <Link
              to="/admin/create-product"
              className="flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700"
            >
              <FiPlus className="mr-2" />
              Add New Product
            </Link>
          </div>

          {/* Stats Cards */}
          <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Products
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.totalProducts}
                  </p>
                </div>
                <div className="rounded-lg bg-indigo-100 p-3">
                  <FiPackage className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Approved</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.approvedProducts}
                  </p>
                </div>
                <div className="rounded-lg bg-green-100 p-3">
                  <FiTrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Revenue
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    ₹{stats.totalRevenue.toFixed(2)}
                  </p>
                </div>
                <div className="rounded-lg bg-blue-100 p-3">
                  <FaIndianRupeeSign className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Out of Stock
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.outOfStock}
                  </p>
                </div>
                <div className="rounded-lg bg-red-100 p-3">
                  <FiCalendar className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-1 flex-col gap-4 sm:flex-row">
                <div className="relative max-w-md flex-1">
                  <FiSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All Status</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-navy-600">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-200">
                    Product
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-200">
                    Pricing
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-200">
                    Inventory
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-200">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-200">
                    Performance
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-200">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredProducts.map((product) => (
                  <React.Fragment key={product._id}>
                    <tr className="transition-colors hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg border border-gray-200">
                            <img
                              className="h-12 w-12 object-cover"
                              src={product.mainImage[0]}
                              alt={product.name}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="max-w-xs truncate text-sm font-semibold text-gray-900">
                              {product.name}
                            </div>
                            <div className="text-xs text-red-600">
                             • {product.gender}
                            </div>
                            <div className="mt-1 text-xs text-gray-400">
                              {product.colors?.length} colors •{" "}
                              {product.sizes?.length} sizes
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex flex-col">
                          <div className="text-sm font-semibold text-gray-900">
                            ₹{product.discountPrice || product.price}
                            {product.discountPrice && (
                              <span className="ml-2 text-xs text-gray-500 line-through">
                                ₹{product.price}
                              </span>
                            )}
                          </div>
                          {product.discountPrice && (
                            <div className="text-xs font-medium text-green-600">
                              {Math.round(
                                ((product.price - product.discountPrice) /
                                  product.price) *
                                  100
                              )}
                              % off
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${getStockColor(
                            product.stock
                          )}`}
                        >
                          <div
                            className={`mr-2 h-2 w-2 rounded-full ${
                              product.stock === 0
                                ? "bg-red-500"
                                : product.stock < 10
                                ? "bg-yellow-500"
                                : "bg-green-500"
                            }`}
                          ></div>
                          {product.stock}{" "}
                          {product.stock === 1 ? "item" : "items"}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <label className="relative inline-flex cursor-pointer items-center">
                          <input
                            type="checkbox"
                            checked={product.status === "approved"}
                            onChange={() =>
                              handleStatusChange(product._id, product.status)
                            }
                            className="peer sr-only"
                          />
                          <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-green-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none"></div>
                        </label>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900">
                            {product.salesCount} sold
                          </div>
                          <div className="text-xs text-gray-500">
                            Revenue: ₹
                            {(
                              (product.discountPrice || product.price) *
                              product.salesCount
                            ).toFixed(2)}
                          </div>
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-center">
                        <div className="flex items-center justify-center space-x-3">
                          <button
                            onClick={() => toggleRow(product._id)}
                            className="text-gray-400 transition-colors hover:text-gray-600"
                            title="View Details"
                          >
                            <FiEye className="h-4 w-4" />
                          </button>
                          <Link
                            to={`/admin/product/edit/${product._id}`}
                            state={{ product }}
                            className="text-indigo-600 hover:text-indigo-900"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FiEdit className="h-5 w-5" />
                          </Link>
                          <button
                            onClick={() => handleDelete(product._id)}
                            className="text-red-600 transition-colors hover:text-red-900"
                            title="Delete"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                          {expandedRows[product._id] ? (
                            <FiChevronUp className="h-4 w-4 text-gray-400" />
                          ) : (
                            <FiChevronDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </td>
                    </tr>

                    {expandedRows[product._id] && (
                      <tr>
                        <td colSpan="6" className="bg-gray-50 px-6 py-6">
                          <div className="max-w-6xl">
                            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                              {/* Product Details */}
                              <div className="rounded-lg border border-gray-200 bg-white p-6">
                                <h3 className="mb-4 flex items-center text-lg font-semibold text-gray-900">
                                  <FiPackage className="mr-2 h-5 w-5 text-indigo-600" />
                                  Product Details
                                </h3>
                                <div className="space-y-3">
                                  <div>
                                    <span className="text-sm font-medium text-gray-700">
                                      Description:
                                    </span>
                                    <p className="mt-1 text-sm text-gray-600">
                                      {product.description}
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <span className="text-sm font-medium text-gray-700">
                                        Colors:
                                      </span>
                                      <div className="mt-1">
                                        {product.colors?.map((color, idx) => (
                                          <span
                                            key={idx}
                                            className="mb-1 mr-1 inline-block rounded bg-gray-100 px-2 py-1 text-xs text-gray-700"
                                          >
                                            {color}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                    <div>
                                      <span className="text-sm font-medium text-gray-700">
                                        Sizes:
                                      </span>
                                      <div className="mt-1">
                                        {product.sizes?.map((size, idx) => (
                                          <span
                                            key={idx}
                                            className="mb-1 mr-1 inline-block rounded bg-gray-100 px-2 py-1 text-xs text-gray-700"
                                          >
                                            {size}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Performance Metrics */}
                              <div className="rounded-lg border border-gray-200 bg-white p-6">
                                <h3 className="mb-4 flex items-center text-lg font-semibold text-gray-900">
                                  <FiTrendingUp className="mr-2 h-5 w-5 text-green-600" />
                                  Performance
                                </h3>
                                <div className="space-y-4">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">
                                      Total Sales
                                    </span>
                                    <span className="text-sm font-semibold text-gray-900">
                                      {product.salesCount}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">
                                      Revenue
                                    </span>
                                    <span className="text-sm font-semibold text-gray-900">
                                      ₹
                                      {(
                                        (product.discountPrice ||
                                          product.price) * product.salesCount
                                      ).toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">
                                      Avg. Order Value
                                    </span>
                                    <span className="text-sm font-semibold text-gray-900">
                                      ₹
                                      {(
                                        product.discountPrice || product.price
                                      ).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Timeline */}
                              <div className="rounded-lg border border-gray-200 bg-white p-6">
                                <h3 className="mb-4 flex items-center text-lg font-semibold text-gray-900">
                                  <FiCalendar className="mr-2 h-5 w-5 text-blue-600" />
                                  Timeline
                                </h3>
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between border-b border-gray-100 py-2">
                                    <span className="text-sm text-gray-600">
                                      Created
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">
                                      {formatDate(product.createdAt)}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between border-b border-gray-100 py-2">
                                    <span className="text-sm text-gray-600">
                                      Last Updated
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">
                                      {formatDate(product.updatedAt)}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between py-2">
                                    <span className="text-sm text-gray-600">
                                      Images
                                    </span>
                                    <div className="flex space-x-2">
                                      {product.gallery
                                        ?.slice(0, 3)
                                        .map((img, idx) => (
                                          <img
                                            key={idx}
                                            src={img}
                                            alt=""
                                            className="h-8 w-8 rounded border border-gray-200 object-cover"
                                          />
                                        ))}
                                      {product.gallery?.length > 3 && (
                                        <div className="flex h-8 w-8 items-center justify-center rounded border border-gray-200 bg-gray-100 text-xs text-gray-500">
                                          +{product.gallery.length - 3}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredProducts.length === 0 && (
          <div className="py-12 text-center">
            <FiPackage className="mx-auto mb-4 h-16 w-16 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              No products found
            </h3>
            <p className="mb-6 text-gray-600">
              Try adjusting your search criteria or add a new product.
            </p>

            <Link
              to="/admin/create-product"
              className="flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700"
            >
              <FiPlus className="mr-2" />
              Add New Product
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Product;
